import { useSyncExternalStore } from 'react';

const queries = {
  sm: '(max-width: 47.9375rem)', // 767px / 16
  md: '(min-width: 48rem) and (max-width: 63.9375rem)', // 768px / 16, 1023px / 16
  lg: '(min-width: 64rem) and (max-width: 79.9375rem)', // 1024px / 16, 1279px / 16
  xl: '(min-width: 80rem) and (max-width: 95.9375rem)', // 1280px / 16, 1535px / 16
  xxl: '(min-width: 96rem)', // 1536px / 16
  minMd: '(min-width: 48rem)', // ≥ 768px
  minLg: '(min-width: 64rem)', // ≥ 1024px
  minXl: '(min-width: 80rem)', // ≥ 1280px
  minXxl: '(min-width: 96rem)', // ≥ 1536px
} as const;

interface MediaQueryStore {
  mediaQuery: MediaQueryList;
  subscribers: Set<() => void>;
  notifySubscribers: () => void;
}

const mediaQueryStores = new Map<string, MediaQueryStore>();

function getMediaQueryStore(query: string): MediaQueryStore {
  const existingStore = mediaQueryStores.get(query);
  if (existingStore) return existingStore;

  const subscribers = new Set<() => void>();
  const store: MediaQueryStore = {
    mediaQuery: window.matchMedia(query),
    subscribers,
    notifySubscribers: () => {
      subscribers.forEach((subscriber) => {
        subscriber();
      });
    },
  };
  mediaQueryStores.set(query, store);
  return store;
}

const subscribe = (callback: () => void, query: string): (() => void) => {
  const store = getMediaQueryStore(query);

  if (store.subscribers.size === 0) {
    store.mediaQuery.addEventListener('change', store.notifySubscribers);
  }
  store.subscribers.add(callback);

  return () => {
    store.subscribers.delete(callback);
    if (store.subscribers.size > 0) return;

    store.mediaQuery.removeEventListener('change', store.notifySubscribers);
    mediaQueryStores.delete(query);
  };
};

const getSnapshot = (query: string): boolean => {
  return getMediaQueryStore(query).mediaQuery.matches;
};

/**
 * Subscribe to whether a CSS media query currently matches.
 * Consumers of the same query share one native MediaQueryList listener.
 *
 * @param query - CSS media query to observe.
 * @returns Whether the query currently matches.
 */
const useBreakpoint = (query: string): boolean => {
  return useSyncExternalStore(
    (callback) => subscribe(callback, query),
    () => getSnapshot(query),
    () => false,
  );
};

export const useBreakpointSm = () => useBreakpoint(queries.sm);
export const useBreakpointMd = () => useBreakpoint(queries.md);
export const useBreakpointLg = () => useBreakpoint(queries.lg);
export const useBreakpointXl = () => useBreakpoint(queries.xl);
export const useBreakpointXxl = () => useBreakpoint(queries.xxl);

export const useBreakpointMinMd = () => useBreakpoint(queries.minMd);
export const useBreakpointMinLg = () => useBreakpoint(queries.minLg);
export const useBreakpointMinXl = () => useBreakpoint(queries.minXl);
export const useBreakpointMinXxl = () => useBreakpoint(queries.minXxl);

export function useBreakpointMap() {
  return {
    sm: useBreakpointSm(),
    md: useBreakpointMd(),
    lg: useBreakpointLg(),
    xl: useBreakpointXl(),
    xxl: useBreakpointXxl(),
    minMd: useBreakpointMinMd(),
    minLg: useBreakpointMinLg(),
    minXl: useBreakpointMinXl(),
    minXxl: useBreakpointMinXxl(),
  };
}

export default useBreakpoint;
